import httpClient from './httpClient';
import { buildQueryString } from '../utils/queryBuilder';
import { parseApiError } from '../utils/errorHandler';

const ENDPOINT = '/api/rotas';

/**
 * Alinhado ao enum backend RouteState (value, status, description).
 */
const ROUTE_STATUS_DEFS = [
    ['CREATION', 'EM CRIAÇÃO', 'O roteiro está em criação.'],
    [
        'PENDING',
        'PLANEJADO',
        'O roteiro foi criado e atribuído ao motorista. Está planejado para execução.',
    ],
    ['IN_PROGRESS', 'EM ANDAMENTO', 'Um motorista está executando o roteiro.'],
    [
        'FINISHED',
        'FINALIZADO',
        'O roteiro foi finalizado. Todos os pontos de entrega foram concluídos.',
    ],
    ['CANCELLED', 'CANCELADO', 'O roteiro foi cancelado.'],
];

export const ROUTE_STATUS = {
    CREATION: 'CREATION',
    /** @deprecated alias — API retorna `CREATION` */
    IN_CREATION: 'CREATION',
    PENDING: 'PENDING',
    IN_PROGRESS: 'PROGRESS',
    FINISHED: 'FINISHED',
    CANCELLED: 'CANCELLED',
};

export const ROUTE_STATUS_LABEL = Object.fromEntries(
    ROUTE_STATUS_DEFS.map(([code, label]) => [code, label]),
);
ROUTE_STATUS_LABEL.IN_CREATION = ROUTE_STATUS_LABEL.CREATION;

export const ROUTE_STATUS_DESCRIPTION = Object.fromEntries(
    ROUTE_STATUS_DEFS.map(([code, _label, description]) => [code, description]),
);
ROUTE_STATUS_DESCRIPTION.IN_CREATION = ROUTE_STATUS_DESCRIPTION.CREATION;

/**
 * @param {string|object|undefined|null} apiStateOrValue
 * @returns {string}
 */
export function getRouteStatusValue(apiStateOrValue) {
    if (!apiStateOrValue) return '';
    if (typeof apiStateOrValue === 'string') return apiStateOrValue;
    if (typeof apiStateOrValue === 'object' && apiStateOrValue.value != null) {
        return String(apiStateOrValue.value);
    }
    return '';
}

/**
 * Normaliza valores legados (`IN_CREATION`) para o enum atual da API (`CREATION`).
 * @param {string|undefined|null} code
 * @returns {string}
 */
export function normalizeRouteStatusValue(code) {
    const key = String(code ?? '').trim();
    if (key === 'IN_CREATION') return ROUTE_STATUS.CREATION;
    if (key === 'IN_PROGRESS') return ROUTE_STATUS.IN_PROGRESS;
    return key;
}

/**
 * @param {string|object|undefined|null} apiStateOrValue
 * @returns {boolean}
 */
export function isRouteInCreation(apiStateOrValue) {
    return normalizeRouteStatusValue(getRouteStatusValue(apiStateOrValue)) === ROUTE_STATUS.CREATION;
}

/**
 * @param {string|object|undefined|null} apiStateOrValue
 * @returns {boolean}
 */
export function isRouteInProgress(apiStateOrValue) {
    return normalizeRouteStatusValue(getRouteStatusValue(apiStateOrValue)) === ROUTE_STATUS.IN_PROGRESS;
}

/**
 * @param {string|object|undefined|null} apiStateOrValue
 * @returns {boolean}
 */
export function isRoutePending(apiStateOrValue) {
    return normalizeRouteStatusValue(getRouteStatusValue(apiStateOrValue)) === ROUTE_STATUS.PENDING;
}

/**
 * @param {string|undefined|null} code
 * @returns {string}
 */
export function getRouteStatusDisplayLabel(code) {
    if (code == null || String(code).trim() === '') return '—';
    const key = normalizeRouteStatusValue(String(code).trim());
    return ROUTE_STATUS_LABEL[key] ?? key;
}

function parseIdFromLocation(locationHeader) {
    if (!locationHeader) return null;
    const trimmed = String(locationHeader).replace(/^\//, '').trim();
    const first = trimmed.split('/')[0];
    const id = Number(first);
    return Number.isFinite(id) ? id : null;
}

export function mapRouteListItem(item) {
    return {
        id: item.id,
        alias: item.alias || `Roteiro #${item.id}`,
        state: item.state,
        driverName: item.driverName || '—',
        pointCount: item.pointCount ?? 0,
        creationDateTime: item.creationDateTime ?? null,
    };
}

/**
 * Formata AddressDTO para exibição.
 * @param {object|null|undefined} addr
 * @returns {string}
 */
export function formatRouteAddress(addr) {
    if (!addr || typeof addr !== 'object') return '—';
    const state = addr.state;
    const stateStr =
        typeof state === 'object' ? state.abbreviation || state.name || '' : state || '';
    const parts = [
        [addr.street, addr.number, addr.complement].filter(Boolean).join(', '),
        [addr.district, addr.city, stateStr].filter(Boolean).join(' — '),
        addr.zipCode,
    ].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
}

/**
 * Extrai os dados da entrega de um ponto (API retorna `deliveryDetails`; spec usa `delivery`).
 * @param {object|null|undefined} point
 * @returns {object|null}
 */
export function getPointDeliveryDetails(point) {
    if (!point || typeof point !== 'object') return null;
    return point.deliveryDetails || point.delivery || null;
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function pickNumericId(value) {
    if (value == null || String(value).trim() === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Busca customerId em objetos aninhados (ex.: endereços da entrega).
 * @param {object|null|undefined} root
 * @param {number} [maxDepth]
 * @returns {number|null}
 */
function findNestedCustomerId(root, maxDepth = 5) {
    if (!root || typeof root !== 'object' || maxDepth < 0) return null;

    const direct = pickNumericId(root.customerId);
    if (direct != null) return direct;

    for (const value of Object.values(root)) {
        if (!value || typeof value !== 'object') continue;
        const nested = findNestedCustomerId(value, maxDepth - 1);
        if (nested != null) return nested;
    }

    return null;
}

/**
 * Extrai o customerId da entrega vinculada ao ponto (necessário em APIs do motorista).
 * @param {object|null|undefined} delivery
 * @returns {number|null}
 */
export function getCustomerIdFromRouteDelivery(delivery) {
    if (!delivery || typeof delivery !== 'object') return null;

    const candidates = [
        delivery.customerId,
        delivery.legalPersonId,
        delivery.legalPersonCustomerId,
        delivery.clientId,
        delivery.customer?.id,
        delivery.customer?.customerId,
        delivery.legalPerson?.id,
        delivery.legalPerson?.customerId,
        delivery.requester?.customerId,
        delivery.requester?.id,
        delivery.creator?.customerId,
        delivery.creator?.id,
        delivery.collectionAddress?.customerId,
        delivery.collectionAddress?.address?.customerId,
        delivery.gatheringAddress?.customerId,
        delivery.gatheringAddress?.address?.customerId,
        delivery.deliveryAddress?.customerId,
        delivery.deliveryAddress?.address?.customerId,
    ];

    for (const candidate of candidates) {
        const id = pickNumericId(candidate);
        if (id != null) return id;
    }

    return findNestedCustomerId(delivery);
}

/**
 * Extrai o customerId a partir do ponto bruto da API (ponto + entrega aninhada).
 * @param {object|null|undefined} point
 * @returns {number|null}
 */
export function getCustomerIdFromRoutePoint(point) {
    if (!point || typeof point !== 'object') return null;

    const fromPoint = pickNumericId(point.customerId ?? point.deliveryCustomerId);
    if (fromPoint != null) return fromPoint;

    return getCustomerIdFromRouteDelivery(getPointDeliveryDetails(point));
}

/**
 * Mapeia deliveryDetails/delivery do ponto para o formato usado nas telas de roteiro.
 * @param {object|null|undefined} delivery
 * @returns {object|null}
 */
export function mapRouteDeliveryToPlanningShape(delivery) {
    if (!delivery || delivery.id == null) return null;

    const collectionWrapper = delivery.collectionAddress || {};
    const deliveryWrapper = delivery.deliveryAddress || {};
    const collectionAddr = collectionWrapper.address;
    const deliveryAddr = deliveryWrapper.address;
    const receiver = delivery.receiver || deliveryWrapper.responsible || {};
    const requester = delivery.requester || {};
    const creator = delivery.creator || {};
    const customer = delivery.customer && typeof delivery.customer === 'object' ? delivery.customer : null;
    const customerName =
        [customer?.name, delivery.customerName, requester.name, creator.name, receiver.name]
            .find((value) => value != null && String(value).trim() !== '') ||
        `Entrega #${delivery.id}`;
    const recipientName = receiver.name || '—';
    const stateVal =
        typeof delivery.state === 'string'
            ? delivery.state
            : delivery.state?.value || '';

    return {
        id: delivery.id,
        customerName: String(customerName).trim(),
        customerType: customer?.type ?? delivery.customerType ?? null,
        customerId: customer?.id ?? delivery.customerId ?? null,
        pickupAddress: formatRouteAddress(collectionAddr),
        deliveryAddress: formatRouteAddress(deliveryAddr),
        pickupInstructions: (collectionWrapper.instructions || '').trim(),
        deliveryInstructions: (deliveryWrapper.instructions || '').trim(),
        recipientName,
        status: stateVal,
        items: Array.isArray(delivery.items) ? delivery.items : [],
    };
}

/**
 * @param {{ page?: number, size?: number, sort?: string, state?: string, driverId?: number|string }} params
 */
async function list({ page = 0, size = 10, sort = 'creationDateTime,desc', state, driverId } = {}) {
    const filters = {};
    if (state) filters.state = state;
    if (driverId != null && driverId !== '') filters.driverId = driverId;

    const queryString = buildQueryString({ page, size, sort, filters });
    const response = await httpClient.get(`${ENDPOINT}?${queryString}`);
    if (!response.ok) {
        throw await parseApiError(response);
    }

    const json = await response.json();
    const content = (json.content || []).map(mapRouteListItem);
    const pageMeta = json.page || {};
    const totalPages = pageMeta.totalPages ?? 0;
    const pageNumber = Math.max(0, pageMeta.number ?? 0);

    return {
        content,
        page: {
            number: pageNumber,
            totalPages,
            first: totalPages === 0 || pageNumber === 0,
            last: totalPages === 0 || pageNumber >= totalPages - 1,
        },
    };
}

/**
 * @param {string|number} id
 */
async function get(id) {
    const response = await httpClient.get(`${ENDPOINT}/${encodeURIComponent(id)}`);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    return response.json();
}

/**
 * @param {{ items: Array<{ deliveryId: number, action: string, index: number, description?: string|null }>, driverId?: number|null, alias?: string|null }} payload
 * @returns {Promise<{ id: number|null }>}
 */
async function create(payload) {
    const response = await httpClient.post(ENDPOINT, payload);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    return { id: parseIdFromLocation(response.headers.get('Location')) };
}

/**
 * @param {string|number} id
 * @param {{ alias?: string|null, driverId?: number|null, removeDriver?: boolean }} payload
 */
async function update(id, payload) {
    const response = await httpClient.put(`${ENDPOINT}/${encodeURIComponent(id)}`, payload);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} id
 */
async function cancel(id) {
    const response = await httpClient.delete(`${ENDPOINT}/${encodeURIComponent(id)}`);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} id
 */
async function plan(id) {
    const response = await httpClient.post(`${ENDPOINT}/${encodeURIComponent(id)}/planejar`, {});
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Finaliza o roteiro (em andamento → finalizado).
 * POST /api/rotas/{id}/finalizar
 *
 * @param {string|number} id
 */
async function finish(id) {
    const response = await httpClient.post(`${ENDPOINT}/${encodeURIComponent(id)}/finalizar`, {});
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} routeId
 * @param {{ deliveryId: number, action: string, index: number, description?: string|null }} payload
 */
async function addPoint(routeId, payload) {
    const response = await httpClient.post(
        `${ENDPOINT}/${encodeURIComponent(routeId)}/pontos`,
        payload,
    );
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const location = response.headers.get('Location');
    const pointId = location ? location.split('/').pop() : null;
    return { pointId: pointId != null ? Number(pointId) : null };
}

/**
 * @param {string|number} routeId
 * @param {string|number} pointId
 * @param {{ action?: string, description?: string|null }} payload
 */
async function updatePoint(routeId, pointId, payload) {
    const response = await httpClient.put(
        `${ENDPOINT}/${encodeURIComponent(routeId)}/pontos/${encodeURIComponent(pointId)}`,
        payload,
    );
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} routeId
 * @param {string|number} pointId
 * @param {string|number} targetPointId
 */
async function swapPointOrder(routeId, pointId, targetPointId) {
    const response = await httpClient.post(
        `${ENDPOINT}/${encodeURIComponent(routeId)}/pontos/${encodeURIComponent(pointId)}/trocar-ordem`,
        { targetPointId: Number(targetPointId) },
    );
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} routeId
 * @param {string|number} pointId
 */
async function removePoint(routeId, pointId) {
    const response = await httpClient.delete(
        `${ENDPOINT}/${encodeURIComponent(routeId)}/pontos/${encodeURIComponent(pointId)}`,
    );
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} routeId
 */
async function start(routeId) {
    const response = await httpClient.post(`${ENDPOINT}/${encodeURIComponent(routeId)}/iniciar`, {});
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} routeId
 * @param {string|number} pointId
 * @param {number} vehicleId
 */
async function updatePointVehicle(routeId, pointId, vehicleId) {
    const response = await httpClient.patch(
        `${ENDPOINT}/${encodeURIComponent(routeId)}/pontos/${encodeURIComponent(pointId)}/veiculo`,
        { vehicleId: Number(vehicleId) },
    );
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} routeId
 * @param {string|number} pointId
 * @param {{ vehicleId: number, comment?: string|null, latitude: number, longitude: number }} payload
 */
async function signalPointSuccess(routeId, pointId, payload) {
    const response = await httpClient.post(
        `${ENDPOINT}/${encodeURIComponent(routeId)}/pontos/${encodeURIComponent(pointId)}/sucesso`,
        payload,
    );
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} routeId
 * @param {string|number} pointId
 * @param {{ vehicleId: number, comment?: string|null, latitude: number, longitude: number }} payload
 */
async function signalPointFailure(routeId, pointId, payload) {
    const response = await httpClient.post(
        `${ENDPOINT}/${encodeURIComponent(routeId)}/pontos/${encodeURIComponent(pointId)}/falha`,
        payload,
    );
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * @param {string|number} routeId
 * @param {string|number} pointId
 * @param {File} file
 * @returns {Promise<{ url: string }>}
 */
async function uploadPointPhoto(routeId, pointId, file) {
    const formData = new FormData();
    formData.append('file', file);

    const headers = new Headers();
    headers.append('X-Tenant-ID', httpClient.tenantId);
    const token = httpClient.token || localStorage.getItem('token');
    if (token) {
        headers.append('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(
        `${httpClient.baseURL}${ENDPOINT}/${encodeURIComponent(routeId)}/pontos/${encodeURIComponent(pointId)}/fotos`,
        { method: 'POST', headers, body: formData },
    );

    if (!response.ok) {
        throw await parseApiError(response);
    }
    return response.json();
}

const routeService = {
    list,
    get,
    create,
    update,
    cancel,
    plan,
    finish,
    addPoint,
    updatePoint,
    swapPointOrder,
    removePoint,
    start,
    updatePointVehicle,
    signalPointSuccess,
    signalPointFailure,
    uploadPointPhoto,
};

export default routeService;
